import { Box } from '@mui/material';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import OperationPreviewIndicator from '~/components/toolbar/OperationsToolbar/OperationPreviewIndicator';
import { ToolPropertiesTraySlotContext } from '~/components/toolbar/shared/ToolPropertiesTray';
import ToolTrayPresenceProvider from '~/components/toolbar/shared/ToolTrayPresenceProvider';
import ContouringStepTools from '~/components/toolbar/steps/ContouringTools';
import DefaultTools from '~/components/toolbar/steps/DefaultTools';
import DoseTools from '~/components/toolbar/steps/DoseTools';
import RegistrationTools from '~/components/toolbar/steps/RegistrationTools';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import { replanningEditingStructuresState } from '~/state/atoms/adaptiveReplanningAtoms';

/** Tools of a workflow step; a step absent here gets {@link DefaultTools}. */
const STEP_TOOLS: Record<string, React.ComponentType> = {
    contouring: ContouringStepTools,
    review: ContouringStepTools,
    registration: RegistrationTools,
    'predictive-planning': DoseTools,
};

/**
 * The tools of the current workflow step, laid out as one horizontal row.
 * Sizes to its content, so a host places it in flow. Renders nothing off a
 * workflow step, where there is no viewport to act on. The row is one
 * {@link ToolTrayPresenceProvider} scope: while a tool tray hangs from it, its
 * buttons show no tooltip.
 *
 * Below the row hangs one column: the open tool tray, then the operation
 * preview statement. They stack in flow, so neither covers a tool or the
 * other at any viewport width.
 */
export default function Toolbar() {
    const { currentStep } = useWorkflowSteps();
    const [traySlot, setTraySlot] = useState<HTMLElement | null>(null);
    const isEditingStructures = useRecoilValue(
        replanningEditingStructuresState
    );

    if (!currentStep) {
        return null;
    }

    // The editing flag outlives leaving the adaptive step, so it only selects contouring tools there.
    const StepTools =
        isEditingStructures && currentStep.id === 'adaptive-replanning'
            ? ContouringStepTools
            : (STEP_TOOLS[currentStep.id] ?? DefaultTools);

    return (
        <Box
            data-toolbar-id="step-toolbar"
            sx={{
                // Positioning parent of the column that hangs from the row's
                // bottom edge.
                position: 'relative',
            }}
        >
            <Box
                sx={{
                    // A hover-only tray floats over the column below, so
                    // the tools stack above it.
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <ToolTrayPresenceProvider>
                    <ToolPropertiesTraySlotContext.Provider value={traySlot}>
                        <StepTools />
                    </ToolPropertiesTraySlotContext.Provider>
                </ToolTrayPresenceProvider>
            </Box>
            <Box
                sx={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    // Sized to its content, not to the half of the row that
                    // an absolute box starting at the row's centre gets.
                    width: 'max-content',
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    pointerEvents: 'none',
                }}
            >
                <Box ref={setTraySlot} sx={{ display: 'contents' }} />
                <OperationPreviewIndicator />
            </Box>
        </Box>
    );
}
